import React, { useState, useRef } from 'react';
import { storageService } from '@/services/storage.service';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';

interface ProjectAttachmentsProps {
  attachments?: string[];
  onAdd?: (url: string) => void;
  onRemove?: (url: string) => void;
  attachmentFiles?: File[];
  setAttachmentFiles?: (files: File[]) => void;
}

export function ProjectAttachments({ attachments = [], onAdd, onRemove, attachmentFiles = [], setAttachmentFiles }: ProjectAttachmentsProps) {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      if (files[i].size > 20 * 1024 * 1024) {
        toast.error(`File ${files[i].name} vượt quá 20MB`);
        return;
      }
    }

    if (setAttachmentFiles) {
      const newFiles = Array.from(files);
      setAttachmentFiles([...attachmentFiles, ...newFiles]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  return (
    <section className="rounded-md bg-white p-4  border border-slate-100 space-y-4">
      <h3 className="text-sm font-normal text-slate-900 tracking-wider">Tài liệu đính kèm</h3>
      
      <div className="space-y-4">
        {/* Existing & Pending Attachments List */}
        {(attachments.length > 0 || attachmentFiles.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Server attachments */}
            {attachments.map((url, index) => {
              const fileName = url.split('/').pop()?.split('-').slice(1).join('-') || `Tài liệu ${index + 1}`;
              const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
              
              return (
                <div key={`server-${index}`} className="flex items-center justify-between p-3 rounded-md border border-slate-100 bg-slate-50/30 group hover:border-emerald-200 transition-all">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "w-10 h-10 rounded-md flex items-center justify-center shrink-0",
                      isImage ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"
                    )}>
                      <i className={isImage ? "ri-image-line text-xl" : "ri-file-text-line text-xl"} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs lg:text-sm font-normal text-slate-700 truncate">{fileName}</span>
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 font-normal hover:underline">Xem chi tiết</a>
                    </div>
                  </div>
                  {onRemove && (
                    <button 
                      type="button" 
                      onClick={() => onRemove(url)}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Pending attachments */}
            {attachmentFiles.map((file, index) => {
              const isImage = file.type.startsWith('image/');
              
              return (
                <div key={`local-${index}`} className="flex items-center justify-between p-3 rounded-md border border-amber-100 bg-amber-50/30 group">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={cn(
                      "w-10 h-10 rounded-md flex items-center justify-center shrink-0",
                      isImage ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"
                    )}>
                      <i className={isImage ? "ri-image-line text-xl" : "ri-file-text-line text-xl"} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs lg:text-sm font-normal text-slate-700 truncate">{file.name}</span>
                      <span className="text-xs text-amber-600 font-normal italic">Chờ tải lên...</span>
                    </div>
                  </div>
                  {setAttachmentFiles && (
                    <button 
                      type="button" 
                      onClick={() => setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index))}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Area */}
        <input 
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
          className="hidden"
        />
        
        <div 
          onClick={handleUploadClick}
          className={cn(
            "border-2 border-dashed border-slate-100 rounded-md p-8 flex flex-col items-center justify-center gap-3 bg-slate-50/20 transition-all cursor-pointer group hover:bg-slate-50/50 hover:border-emerald-200"
          )}
        >
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform">
             <i className="ri-upload-cloud-2-line text-xl text-emerald-600" />
          </div>
          <div className="text-center">
            <p className="text-xs lg:text-sm font-normal text-slate-600">
              Kéo thả file vào đây hoặc <button type="button" className="text-emerald-600 font-normal hover:underline pointer-events-none">Chọn file</button>
            </p>
          </div>
        </div>
        
        <span className="text-xs text-slate-400 font-normal">{attachments.length + attachmentFiles.length}/150</span>
        <p className="text-xs text-slate-400 font-normal italic">
          Hỗ trợ: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Tối đa 20MB)
        </p>
      </div>
    </section>
  );
}
